/**
 * Serialize selected market ids for list/insights query params and cache keys.
 * Order does not change the filter; duplicates collapse. A single id stays a
 * single id so existing `?market=<id>` URLs keep working.
 */
export function serializeMarketFilter(
  market: string | readonly string[] | null | undefined,
): string | undefined {
  if (market == null) return undefined;
  const ids = (typeof market === "string" ? market.split(",") : [...market])
    .map((id) => id.trim())
    .filter(Boolean);
  if (ids.length === 0) return undefined;
  return [...new Set(ids)].sort().join(",");
}

/**
 * A cached list covers a new report when it is all-markets or shares any
 * selected market with the report's joins.
 */
export function listScopeIncludesMarkets(
  scope: string | readonly string[] | null | undefined,
  coveringMarketIds: readonly string[],
): boolean {
  const serialized = serializeMarketFilter(scope);
  if (!serialized) return true;
  return serialized.split(",").some((id) => coveringMarketIds.includes(id));
}

/**
 * Empty-state count for the hub header. All Markets uses the unique tenant
 * total. One market uses that market's membership count. Several markets
 * cannot be summed without double-counting, so this only answers "any?".
 */
export function reportsInSelectedScope(
  markets: readonly { id: string; reportCount: number }[],
  allMarketsReportCount: number,
  selectedIds: readonly string[] | null,
): number {
  if (selectedIds == null || selectedIds.length === 0) return allMarketsReportCount;
  if (selectedIds.length === 1) {
    return markets.find((market) => market.id === selectedIds[0])?.reportCount ?? 0;
  }
  return selectedIds.some(
    (id) => (markets.find((market) => market.id === id)?.reportCount ?? 0) > 0,
  )
    ? 1
    : 0;
}
